import { Input } from "@/components/ui/input"
import { Select, MultiSelect, type SelectOption } from "@/components/ui/select"
import {
  pickChoices,
  type CustomFieldLike,
  type DisplayOp,
  type FieldKind,
} from "@/lib/predicate-ast"

// Type-aware value control for one predicate row. The control is hidden
// entirely for `present` / `blank` ops (they have no value). For
// `between`, two stacked inputs feed a `[lo, hi]` pair.

type Props = {
  fieldType: FieldKind
  fieldDef: CustomFieldLike | null
  op: DisplayOp
  value: unknown
  onChange: (next: unknown) => void
  disabled?: boolean
}

export function ValueControl({ fieldType, fieldDef, op, value, onChange, disabled }: Props) {
  if (op === "present" || op === "blank") return null

  if (op === "between") {
    return <BetweenPair fieldType={fieldType} value={value} onChange={onChange} disabled={disabled} />
  }

  if (fieldType === "boolean") {
    return (
      <Select<string>
        value={
          value === true
            ? { value: "true", label: "true" }
            : value === false
              ? { value: "false", label: "false" }
              : null
        }
        onChange={(o) => onChange(o?.value === "true")}
        options={[
          { value: "true",  label: "true" },
          { value: "false", label: "false" },
        ]}
        isDisabled={disabled}
        isSearchable={false}
      />
    )
  }

  if (fieldType === "select" || fieldType === "system_state") {
    const choices = fieldDef ? pickChoices(fieldDef) : []
    const opts: SelectOption<string>[] = choices.map((c) => ({ value: c.value, label: c.label }))
    return (
      <Select<string>
        value={opts.find((o) => o.value === value) ?? null}
        onChange={(o) => onChange(o ? o.value : "")}
        options={opts}
        isDisabled={disabled}
        placeholder={opts.length === 0 ? "No choices configured" : "Pick a value…"}
      />
    )
  }

  if (fieldType === "multi_select") {
    const choices = fieldDef ? pickChoices(fieldDef) : []
    const opts: SelectOption<string>[] = choices.map((c) => ({ value: c.value, label: c.label }))
    const arr = Array.isArray(value) ? (value as string[]) : []
    const selected = arr
      .map((v) => opts.find((o) => o.value === v))
      .filter((o): o is SelectOption<string> => Boolean(o))
    return (
      <MultiSelect<string>
        value={selected}
        options={opts}
        onChange={(next) => {
          const sel = (next ?? []) as readonly SelectOption<string>[]
          onChange(sel.map((o) => o.value))
        }}
        isDisabled={disabled}
        placeholder="Pick one or more…"
      />
    )
  }

  if (fieldType === "number") {
    return (
      <Input
        type="number"
        value={value === null || value === undefined ? "" : String(value)}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value
          onChange(v === "" ? "" : Number(v))
        }}
        className="w-full"
      />
    )
  }

  if (fieldType === "date") {
    return (
      <Input
        type="date"
        value={typeof value === "string" ? value : ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full"
      />
    )
  }

  // text / fallback
  return (
    <Input
      type="text"
      value={typeof value === "string" ? value : value == null ? "" : String(value)}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-full"
    />
  )
}

function BetweenPair({
  fieldType, value, onChange, disabled,
}: {
  fieldType: FieldKind
  value: unknown
  onChange: (next: unknown) => void
  disabled?: boolean
}) {
  const pair = Array.isArray(value) && value.length === 2
    ? (value as [unknown, unknown])
    : ["", ""] as [unknown, unknown]
  const [lo, hi] = pair
  const isDate = fieldType === "date"
  const inputType = isDate ? "date" : "number"
  const cast = (v: string) => (v === "" ? "" : isDate ? v : Number(v))

  return (
    <div className="flex items-center gap-1.5">
      <Input
        type={inputType}
        value={lo == null ? "" : String(lo)}
        disabled={disabled}
        onChange={(e) => onChange([cast(e.target.value), hi])}
        className="w-full"
      />
      <span className="text-xs text-app-muted">and</span>
      <Input
        type={inputType}
        value={hi == null ? "" : String(hi)}
        disabled={disabled}
        onChange={(e) => onChange([lo, cast(e.target.value)])}
        className="w-full"
      />
    </div>
  )
}
